import React, { useState } from 'react';
import styled from 'styled-components';
import { getDefaultCareerSections, resolveCareerSections } from '../CareerData';
import { useCareer } from '../../../shared/hooks/useApi/useCareer';
import { CareerType, CareerPeriodType } from '../../../shared/enum/EnumRepository';
import { careerBuilder } from '../../../shared/converter/entityBuilder';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { PaperBlurFitBtn } from '../../../shared/component/headless/button/BtnBody';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { useSettingStore } from '../../../shared/store/settingStore';
import upIcon from '../../../asset/icon/up.png';
import belowIcon from '../../../asset/icon/below.png';
import { useAboutPageSettingState } from '../../../shared/hooks/useApi/useAboutPageSetting';
import { useCanShowAboutPageSetting } from '../../../shared/hooks/useAboutPageSettingVisibility';


const CreateCareerMobile: React.FC = () => {
  const { createCareer } = useCareer();
  const setting = useSettingStore(s => s.setting);
  const { aboutPageSetting } = useAboutPageSettingState();
  const canShowAboutPageSetting = useCanShowAboutPageSetting();
  const [showAllCareerTypes, setShowAllCareerTypes] = useState(false);
  const orderedCareerSections = resolveCareerSections(
    canShowAboutPageSetting ? aboutPageSetting?.careerSectionOrder : null,
  );
  const defaultCareerSections = getDefaultCareerSections(
    setting,
    orderedCareerSections,
  );
  const defaultCareerTypes = new Set(
    defaultCareerSections.map((section) => section.type),
  );
  const additionalCareerSections = orderedCareerSections.filter(
    (section) => !defaultCareerTypes.has(section.type),
  );
  
  const handleCreateElement = async (careerType: CareerType, range: CareerPeriodType) => {
    await createCareer(careerBuilder(careerType, range));
  };

  const renderCreateButton = () => {
      return (
        <CreateButtonGroup>
          {defaultCareerSections.map((section) => (
            <HeadlessBtn
              key={section.type}
              value={section.title}
              handleClick={() => handleCreateElement(section.type, section.storageRange)}
              StyledBtn={CareerOptionPaperBtn}
            />
          ))}
          {additionalCareerSections.map((section) => (
            <ExpandableCell
              key={section.type}
              $isExpanded={showAllCareerTypes}
              aria-hidden={!showAllCareerTypes}
            >
              <ExpandableCellInner>
                <HeadlessBtn
                  value={section.title}
                  handleClick={() => handleCreateElement(section.type, section.storageRange)}
                  StyledBtn={CareerOptionPaperBtn}
                />
              </ExpandableCellInner>
            </ExpandableCell>
          ))}
          {defaultCareerSections.length < orderedCareerSections.length && (
            <CareerTypeToggle
              type="button"
              aria-label={showAllCareerTypes ? 'Collapse career types' : 'Show all career types'}
              aria-expanded={showAllCareerTypes}
              onClick={() => setShowAllCareerTypes((current) => !current)}
            >
              <CareerTypeToggleIcon
                $backgroundImg={showAllCareerTypes ? upIcon : belowIcon}
              />
            </CareerTypeToggle>
          )}
        </CreateButtonGroup>
      );
  }

  return (
    <CreateCareerComp>
      <TitleWrapper>
        <Title>{getModalMessage('CREATE_CAREER_MOBILE')}</Title>         
      </TitleWrapper>
      {renderCreateButton()}
    </CreateCareerComp>
  );
};


const CreateCareerComp = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
`

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`

const CreateButtonGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: clamp(12px, 2vw, 16px);
  row-gap: 0;
`;

const CareerOptionPaperBtn = styled(PaperBlurFitBtn)`
  width: 100%;
  margin-bottom: clamp(12px, 2vw, 16px);
`;

const ExpandableCell = styled.div<{ $isExpanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? '1fr' : '0fr')};
  min-width: 0;
  min-height: 0;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  visibility: ${({ $isExpanded }) => ($isExpanded ? 'visible' : 'hidden')};
  pointer-events: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'none')};
  transition:
    grid-template-rows 0.45s ease-in-out,
    opacity 0.3s ease-in-out,
    visibility 0s ${({ $isExpanded }) => ($isExpanded ? '0s' : '0.45s')};
`;

const ExpandableCellInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const CareerTypeToggle = styled(PaperBlurFitBtn)`
  width: 100%;
  grid-column: 1 / -1;
`;

const CareerTypeToggleIcon = styled.span<{ $backgroundImg: string }>`
  display: block;
  width: 8px;
  height: 8px;
  background-image: url(${({ $backgroundImg }) => $backgroundImg});
  background-size: cover;
  background-position: center;
`;

export default CreateCareerMobile;
